/* Пакет трансмиттала: zip собирается в браузере.
 *
 * Сервер отдаёт манифест — подписанные ссылки на файлы состава и листы CRS, —
 * а байты идут из хранилища напрямую в браузер и складываются в архив здесь.
 * Прогонять десятки мегабайт чертежей через JVM значило бы сделать канал
 * приложения узким местом (тот же довод, что у одиночного скачивания,
 * FileController).
 *
 * ФОРМЫ В АРХИВЕ — СЕРВЕРНЫЕ. Здесь собирался свой бланк трансмиттала и свои
 * листы CRS: табличка «поле — значение», похожая на бланк заказчика ровно
 * настолько, насколько её успели написать. Получатель сверял её со своим
 * шаблоном и не находил ни блока цели выпуска, ни подписей сторон, ни колонок
 * своего бланка CRS. Бланки лежат у сервера, он их и заполняет: у входящего и
 * исходящего они разные (xlsx и docx), и второго набора правил быть не должно.
 *
 * Опись пакета собирается ЗДЕСЬ: она описывает архив, а не письмо, и знает то,
 * чего сервер знать не может, — что из перечисленного действительно легло в
 * архив и по какому пути.
 *
 * Сбой по одному файлу не роняет пакет: файл попадает в список неудач и в
 * описи помечается «нет» — молча уменьшившийся архив хуже отказа.
 */
import JSZip from 'jszip'
import * as XLSX from 'xlsx'
import { request, requestFile } from '@/data/api'
import { buildPackageIndex, type PackageFileRow, type PackageSheetRow, type TrmFormHead } from '@/lib/excel'
import { t } from '@/i18n/lang'
import type { Lang } from '@/i18n/lang'

export interface PackageManifest {
  id: string
  dir: 'in' | 'out'
  /* Ссылка на объект хранилища есть только у ДЕЙСТВУЮЩЕЙ ревизии: у
     недействующей сервер отдаёт адрес помеченной копии, а сырого объекта не
     даёт вовсе — иначе с площадки резали бы металл по листу без пометки. */
  files: {
    doc: string; rev: string; ver: number; kind: string; name: string; size: number
    url: string | null
    controlled?: string
    notice?: string | null
  }[]
  /* Лист замечаний называется не одним кодом: опись обязана показать документ,
     ревизию, число замечаний и состояние листа. */
  sheets: { code: string; doc: string; rev?: string; ver?: number; comments?: number; status?: string }[]
  /* Чертежи с нанесённой разметкой: их собирает сервер, поэтому здесь только
     адрес документа, а не подписанная ссылка хранилища. */
  marked?: { doc: string; rev: string; ver: number }[]
}

export interface PackageResult {
  files: number
  sheets: number
  failed: string[]
}

export async function downloadTrmPackage(trm: TrmFormHead, docs: unknown[], lang: Lang): Promise<PackageResult> {
  const manifest = await request<PackageManifest>(
    'GET', `/transmittals/${encodeURIComponent(trm.id)}/package/manifest`)

  const zip = new JSZip()
  const failed: string[] = []
  /* Наименование документа в описи — из состава письма: манифест перечисляет
     файлы, а как документ называется, знает строка реестра. */
  const titleOf = (no: string): string => {
    const row = (docs as { no?: string; title?: string }[]).find((d) => d?.no === no)
    return row?.title ?? ''
  }

  /* Бланк заказчика — первым файлом архива: с него начинают чтение пакета.
     Собирает сервер, здесь только раскладка по имени, которое он назвал. */
  let formName = ''
  try {
    const { blob, fileName } = await requestFile(`/transmittals/${encodeURIComponent(trm.id)}/form`)
    formName = fileName
    zip.file(fileName, blob)
  } catch {
    failed.push(t(lang, 'бланк трансмиттала', 'transmittal form'))
  }

  const fileRows: PackageFileRow[] = []
  let files = 0
  for (const f of manifest.files) {
    const path = `${f.doc}/v${f.ver}_${f.name}`
    let packed = false
    try {
      if (f.controlled) {
        /* Недействующая ревизия собирается сервером с пометкой на каждом
           листе: подписанной ссылки на сырой объект у неё нет, и в архив
           обязана попасть именно помеченная копия. */
        const { blob } = await requestFile(f.controlled.replace(/^\/api/, ''))
        zip.file(path, blob)
      } else {
        /* Голый fetch: ссылка уже подписана, заголовок с токеном сломал бы подпись. */
        const res = await fetch(f.url ?? '')
        if (!res.ok) throw new Error(`GET ${res.status}`)
        // Версии одной ревизии носят одно имя — раскладываются по папкам версий.
        zip.file(path, await res.arrayBuffer())
      }
      packed = true
      files++
    } catch {
      failed.push(path)
    }
    fileRows.push({
      doc: f.doc, title: titleOf(f.doc), rev: f.rev, ver: f.ver, kind: f.kind,
      name: f.name, size: f.size, path, notice: f.notice ?? null, packed,
    })
  }

  const sheetRows: PackageSheetRow[] = []
  let sheets = 0
  for (const s of manifest.sheets) {
    /* Бланк CRS тоже серверный: у заказчика он свой, с колонками ответа
       подрядчика, и собранная здесь табличка ему не отвечала. */
    let path = `CRS/${s.code}.xlsx`
    let packed = false
    try {
      const { blob, fileName } = await requestFile(`/crs-sheets/${encodeURIComponent(s.code)}/form`)
      path = `CRS/${fileName}`
      zip.file(path, blob)
      packed = true
      sheets++
    } catch {
      failed.push(s.code)
    }
    sheetRows.push({
      code: s.code, doc: s.doc, rev: s.rev, ver: s.ver,
      comments: s.comments, status: s.status, path, packed,
    })
  }

  /* Разметка уезжает файлом: подрядчик правит модель по обведённым местам, а
     не по списку замечаний. Сбой по одному листу не роняет пакет. */
  for (const m of manifest.marked ?? []) {
    const label = t(lang, 'разметка', 'markup')
    try {
      const { blob, fileName } = await requestFile(
        `/docs/${encodeURIComponent(m.doc)}/revisions/${encodeURIComponent(m.rev)}/versions/${m.ver}/markup.pdf`)
      const path = `${m.doc}/${fileName}`
      zip.file(path, blob)
      files++
      fileRows.push({
        doc: m.doc, title: titleOf(m.doc), rev: m.rev, ver: m.ver, kind: label,
        name: fileName, size: blob.size, path, packed: true,
      })
    } catch {
      failed.push(`${m.doc} (${label})`)
      fileRows.push({
        doc: m.doc, title: titleOf(m.doc), rev: m.rev, ver: m.ver, kind: label,
        name: 'markup.pdf', size: 0, path: `${m.doc}/markup.pdf`, packed: false,
      })
    }
  }

  /* Опись — последней: она отвечает за то, что в архиве ЕСТЬ, и собрать её до
     раскладки значило бы описать намерение, а не пакет. */
  const indexName = `${trm.id}_${t(lang, 'опись', 'index')}.xlsx`
  zip.file(indexName, XLSX.write(buildPackageIndex(trm, fileRows, sheetRows, lang), { type: 'array' }) as ArrayBuffer)

  const blob = await zip.generateAsync({ type: 'blob' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `${trm.id}_package.zip`
  a.click()
  URL.revokeObjectURL(a.href)
  return { files: files + (formName ? 1 : 0), sheets, failed }
}
